import React, { useState } from "react";

import { Box, Grid } from "@mui/material";
import { Stack } from "@mui/material";
import classnames from "classnames";
import lodash from "lodash";
import { useTranslation } from "react-i18next";
import { BsBoxSeam } from "react-icons/bs";
import { FiPenTool } from "react-icons/fi";
import * as yup from "yup";

import Checkbox from "../../../../../../components/Form/Checkbox";
import RadioGroup from "../../../../../../components/Form/RadioGroup";
import ActionButtons from "../../../../../../components/Generic/ActionButtons";
import BadgeType from "../../../../../../components/Generic/BadgeType";
import EstimateInventorySection from "../../../../../../components/Generic/Estimate/InventorySection";
import LoadingModal from "../../../../../../components/LoadingModal";
import { ExternalList } from "../../../../../../context/MovingResources";
import useYupValidation from "../../../../../../hooks/useYupValidation";
import { Destination } from "../../../../../../models/destination";
import { Estimate } from "../../../../../../models/estimate";
import { Location } from "../../../../../../models/location";
import { Material } from "../../../../../../models/material";
import { Sales as SalesQuote, RoomJob, QuotedItem } from "../../../../../../models/sales";
import { convertMetricVolume, handleGetConvertValue } from "../../../../../../util/converts";
import { DESTINATION, PaymentOption } from "../../../../../../util/enums";
import { getMeasurement } from "../../../../../../util/getMeasurement";
import SignatureField from "./SignatureField";
import useStyles from "./styles";
import SummaryCosting from "./SummariCosting";
import SummaryLocation from "./SummaryLocation";

interface Props {
  data?: SalesQuote;
  handleUpdate?: (attribute: string, value: string | boolean) => void;
  onChangeEstimate?: (estimate?: Estimate, hasChanged?: boolean) => void;
  onCancel?: () => void;
  onSave?: () => void;
  onSaveAndSend?: () => void;
  isOnViewMode?: boolean;
  isUpdating?: boolean;
  errors?: yup.ValidationError[];
  externalList?: ExternalList;
  isSavingAndSending?: boolean;
}

const SummaryTab = (props: Props) => {
  const {
    data,
    handleUpdate,
    errors = [],
    onCancel,
    onSave,
    onChangeEstimate,
    onSaveAndSend,
    isUpdating,
    isOnViewMode = false,
    isSavingAndSending = false,
  } = props;

  const classes = useStyles();
  const { t: translate } = useTranslation();

  const [destinationList] = useState<Destination[]>(props?.externalList?.destinationList || []);
  const [signatureConsultant, setSignatureConsultant] = useState<boolean>(
    !!data?.signatureConsultant || false
  );
  const [signatureCustomer, setSignatureCustomer] = useState<boolean>(
    !!data?.signatureCustomer || false
  );

  const { fieldHasError } = useYupValidation(errors);

  const measurement = getMeasurement();
  const consultantName = data?.user ? `${data?.user.name} ${data?.user.lastName}` : "";
  const inventory = data?.inventory || [];

  const returnTotalVolumeOfItem = (item: QuotedItem, total: number): number => {
    if (item.itemJob.destination?.name === DESTINATION.NOT_GOING) return total;

    return (
      total +
      Number(convertMetricVolume(item.itemJob.volume, measurement?.code, 1)) *
        Number(item.itemJob.quantity)
    );
  };

  const returnTotalVolumeOfMaterial = (material: Material, total: number): number => {
    if (material.serviceBooking.isItem && material.destination?.name !== DESTINATION.NOT_GOING) {
      if (material.volume) return total + Number(material.volume) * Number(material.quantity);
    }

    return total;
  };

  const volumeInventory = () => {
    let totalOfItems = 0;
    let totalOfMaterials = 0;

    inventory.forEach((roomJob: RoomJob) => {
      totalOfItems += roomJob.itemsJob.reduce(
        (total: number, item: QuotedItem) => returnTotalVolumeOfItem(item, total),
        0
      );
      totalOfMaterials += roomJob.materials.reduce(
        (total: number, material: Material) => returnTotalVolumeOfMaterial(material, total),
        0
      );
    });

    return Number(totalOfItems + totalOfMaterials);
  };

  const badgeInventory = (value: number) => {
    return (
      <div className={classes.badge}>
        <BsBoxSeam size={12} />
        <span className={classes.textBadge}>{`${handleGetConvertValue(
          value,
          measurement?.code,
          measurement?.decimalPlaces
        )} ${measurement?.code === "ft³" ? "FT3" : "M3"}`}</span>
      </div>
    );
  };

  const onChangePaymentMethod = (payType: string) => {
    let estimate = data?.estimate;

    if (!estimate) estimate = { prices: [], paymentMethod: "" };

    const deep = lodash.cloneDeep(estimate);

    if (onChangeEstimate) {
      deep!.paymentMethod = payType;
      onChangeEstimate(deep);
    }
  };

  return (
    <div>
      <div className={classes.container}>
        <Stack direction="row" justifyContent={"space-between"}>
          <span className={classes.title}>{translate("summary")}</span>
          <Box>
            <Stack direction={"row"} spacing={1}>
              <BadgeType value={data?.jobType?.name || translate("empty")}></BadgeType>
              {badgeInventory(volumeInventory())}
            </Stack>
          </Box>
        </Stack>

        <SummaryLocation
          origin={data?.origin?.locations as Location[]}
          destination={data?.destination?.locations as Location[]}
        />

        <EstimateInventorySection
          destinationList={destinationList}
          destinationAddresses={(data?.destination?.locations || []) as Location[]}
          inventory={inventory}
          measurementCode={measurement?.code}
        />

        <div className={classes.separator} />

        <Grid container direction="column">
          <Grid item>
            <span className={classes.label}>{translate("consultant")}</span>
          </Grid>
          <Grid item className={classes.consultantName}>
            <span>{consultantName}</span>
          </Grid>
        </Grid>

        <Grid container className={classes.totalCostContainer} direction={"column"}>
          <Grid item>
            <span className={classes.label}>{translate("costing")}</span>
          </Grid>
          <Grid item>
            <SummaryCosting inventory={inventory} prices={data?.estimate?.prices || []} />
          </Grid>
        </Grid>

        <Grid container direction="column" className={classes.paymentContainer}>
          <Grid item className={classes.mb16}>
            <span className={classes.label}>{translate("paymentMethod")}</span>
          </Grid>
          <Grid item>
            <RadioGroup
              disabled={isOnViewMode}
              options={[
                {
                  key: PaymentOption.ACCOUNT,
                  label: translate("account"),
                  "data-cy": "payment-account",
                  value: PaymentOption.ACCOUNT,
                },
                {
                  key: PaymentOption.CREDIT_CARD,
                  label: translate("creditCard"),
                  "data-cy": "payment-credit-card",
                  value: PaymentOption.CREDIT_CARD,
                },
                {
                  key: PaymentOption.CASH,
                  label: translate("cash"),
                  "data-cy": "payment-cash",
                  value: PaymentOption.CASH,
                },
              ]}
              value={data?.estimate?.paymentMethod}
              onChange={(event) => onChangePaymentMethod(event.target.value)}
              error={fieldHasError("paymentMethod")}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2} className={classes.w50}>
          <Grid item container>
            <span className={classes.label}>{translate("signature")}</span>
          </Grid>
          <Grid item container spacing={4}>
            <Grid item>
              <Stack direction="column">
                {signatureCustomer || isOnViewMode ? (
                  <SignatureField
                    disabled={isOnViewMode}
                    handleUpdate={(attribute: string, value: string | boolean) => {
                      if (handleUpdate) handleUpdate(attribute, value);
                    }}
                    signature={data?.signatureCustomer}
                    signatureOwner={"signatureCustomer"}
                  />
                ) : (
                  <>
                    <div
                      className={
                        fieldHasError("signatureCustomer")
                          ? classnames(classes.buttonSign, classes.buttonError)
                          : classnames(classes.buttonSign)
                      }
                    >
                      <div
                        className={classes.centralize}
                        onClick={() => setSignatureCustomer(true)}
                      >
                        <FiPenTool className={classes.mr5} />
                        <span className={classes.mr5}>{`
                   ${translate("signHere")}`}</span>
                      </div>
                    </div>
                    <span className={classes.signatureName}>{translate("customer")}</span>
                  </>
                )}
              </Stack>
            </Grid>
            <Grid item>
              <Stack direction="column">
                {signatureConsultant || isOnViewMode ? (
                  <SignatureField
                    disabled={isOnViewMode}
                    handleUpdate={(attribute: string, value: string | boolean) => {
                      if (handleUpdate) handleUpdate(attribute, value);
                    }}
                    signature={data?.signatureConsultant}
                    signatureOwner={"signatureConsultant"}
                  />
                ) : (
                  <>
                    <div
                      className={
                        fieldHasError("signatureConsultant")
                          ? classnames(classes.buttonSign, classes.buttonError)
                          : classnames(classes.buttonSign)
                      }
                    >
                      <div
                        className={classes.centralize}
                        onClick={() => setSignatureConsultant(true)}
                      >
                        <FiPenTool className={classes.mr5} />
                        <span className={classes.mr5}>{`
                   ${translate("signHere")}`}</span>
                      </div>
                    </div>
                    <span className={classes.signatureName}>{translate("consultant")}</span>
                  </>
                )}
              </Stack>
            </Grid>
          </Grid>
        </Grid>
        <div className={classes.smallSpacing}>
          <Checkbox
            disabled={isOnViewMode}
            label={translate("iConfirmThatAllTheDetailsAboveAreTrue")}
            checked={!!data?.summaryConfirmed}
            onChange={(event, checked) => {
              if (handleUpdate) {
                handleUpdate("summaryConfirmed", checked);
              }
            }}
            error={fieldHasError("summaryDetails")}
          />
        </div>

        {!isOnViewMode && (
          <div className={classes.buttons}>
            <ActionButtons
              showSaveAndSendButton={isSavingAndSending}
              cancelDisabled={isUpdating}
              saveDisabled={isUpdating}
              onSave={onSave}
              onCancel={onCancel}
              onSaveAndSend={onSaveAndSend}
            />
          </div>
        )}
      </div>
      <LoadingModal showing={isUpdating} />
    </div>
  );
};

export default SummaryTab;
